import React, { FunctionComponent } from 'react';
import { Public } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { PrimitiveChange } from '@upstart/harmony/lib/legos/propertyChangeTypes';
import { FinanceType } from '@upstart/harmony/lib/constants';
import { formatName, formatValue } from './propertyFormatter';
import { FINANCE_TYPE_PROPERTIES, GLOBAL_PROPERTIES } from '../Constants';
import { ChangeDescription, ChangeType } from './ChangeDescription';
import './PropertyChange.scss';

interface PrimitivePropertyProps {
  change: PrimitiveChange;
  financeType: FinanceType;
}

/**
 * Sample output:
 * <div>APR</div>
 * <ul>
 *   <li>
 *     Changed APR from <span className="offer-prop-change-value">3.99%</span>
 *     to <span className="offer-prop-change-value">4.99%</span>
 *   </li>
 * </ul>
 */
export const PrimitiveProperty: FunctionComponent<PrimitivePropertyProps> = ({
  change,
  financeType,
}) => {
  const oldValue = formatValue({ name: change.name, value: change.oldValue });
  const newValue = formatValue({ name: change.name, value: change.value });
  const isFinance = FINANCE_TYPE_PROPERTIES.includes(change.name);
  const isGlobal = GLOBAL_PROPERTIES.includes(change.name);

  return (
    <div>
      {isFinance || isGlobal ? (
        <div className="offer-prop-change-name-container">
          <div>{formatName(change.name)}</div>
          <div>
            <Tooltip
              title={
                <span style={{ fontSize: '12px' }}>
                  Global Change:
                  <br />
                  Applied to all {isFinance ? formatName(financeType) : ''}{' '}
                  pencils
                </span>
              }
              placement="left"
              arrow
            >
              <Public fontSize="medium" />
            </Tooltip>
          </div>
        </div>
      ) : (
        <div>{formatName(change.name, financeType)}</div>
      )}
      <ul className="offer-prop-change-detail">
        <ChangeDescription
          changeType={ChangeType.CHANGE}
          name={change.name}
          values={[oldValue, newValue]}
          showName={false}
        />
      </ul>
    </div>
  );
};

export const isPropertyChanged = (change: PrimitiveChange) => {
  const oldValue = formatValue({ name: change.name, value: change.oldValue });
  const newValue = formatValue({ name: change.name, value: change.value });
  return oldValue !== newValue;
};
